import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Top Navigation Component
 * Mobile-responsive with hamburger menu
 * Displays user info, role badge, and logout functionality
 */
const TopNav: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      setIsMobileMenuOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-500 text-white';
      case 'USER':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'USER':
        return 'User';
      default:
        return role;
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <nav className="bg-neutral-900 border-b border-neutral-800 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Brand */}
          <Link to="/dashboard" className="text-lg sm:text-xl font-bold text-white hover:text-cyan-400 transition-colors flex-shrink-0">
            The Traders
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 mx-8">
            <Link
              to="/dashboard"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/dashboard' 
                  ? 'text-cyan-400' 
                  : 'text-neutral-300 hover:text-white'
              }`}
            >
              Dashboard
            </Link>
            {user.role !== 'ADMIN' && (
              <Link
                to="/alpaca-keys"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/alpaca-keys' 
                    ? 'text-cyan-400' 
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                Alpaca Keys
              </Link>
            )}
            <Link
              to="/trade-history"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/trade-history' 
                  ? 'text-cyan-400' 
                  : 'text-neutral-300 hover:text-white'
              }`}
            >
              Trade History
            </Link>
            <Link
              to="/logs"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/logs' 
                  ? 'text-cyan-400' 
                  : 'text-neutral-300 hover:text-white'
              }`}
            >
              Activity & Logs
            </Link>
            {user.role === 'ADMIN' && (
              <>
                <Link
                  to="/admin"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/admin' 
                      ? 'text-cyan-400' 
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  Admin
                </Link>
                <Link
                  to="/admin/system-health"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin/system-health')
                      ? 'text-cyan-400'
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  System Health
                </Link>
                <Link
                  to="/admin/market-screening"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin/market-screening')
                      ? 'text-cyan-400'
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  Market Screening
                </Link>
              </>
            )}
          </div>

          {/* Desktop User Info and Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm text-neutral-300">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-neutral-500">{user.email}</div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                {getRoleDisplayName(user.role)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Link
                to="/settings"
                className={`text-sm px-3 py-2 rounded-lg transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-cyan-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
              >
                Settings
              </Link>
              <button
                onClick={handleLogout}
                className="text-sm px-4 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-neutral-300 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors min-w-touch min-h-touch flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile/Tablet Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={closeMobileMenu}
        >
          <div 
            className="absolute right-0 top-0 h-full w-full sm:w-80 bg-neutral-900 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-800">
              <h2 className="text-lg font-semibold text-white">Menu</h2>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-neutral-300 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors min-w-touch min-h-touch flex items-center justify-center"
                aria-label="Close menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-neutral-800">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-xs text-neutral-400">{user.email}</div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </span>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="p-4 space-y-2">
              <Link
                to="/dashboard"
                onClick={closeMobileMenu}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                  location.pathname === '/dashboard'
                    ? 'bg-cyan-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
              >
                Dashboard
              </Link>
              {user.role !== 'ADMIN' && (
                <Link
                  to="/alpaca-keys"
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                    location.pathname === '/alpaca-keys'
                      ? 'bg-cyan-600 text-white'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  Alpaca Keys
                </Link>
              )}
              <Link
                to="/trade-history"
                onClick={closeMobileMenu}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                  location.pathname === '/trade-history'
                    ? 'bg-cyan-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
              >
                Trade History
              </Link>
              <Link
                to="/logs"
                onClick={closeMobileMenu}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                  location.pathname === '/logs'
                    ? 'bg-cyan-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
              >
                Activity & Logs
              </Link>
              {user.role === 'ADMIN' && (
                <Link
                  to="/admin"
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                    location.pathname === '/admin'
                      ? 'bg-cyan-600 text-white'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  Admin
                </Link>
              )}
              {user.role === 'ADMIN' && (
                <Link
                  to="/admin/system-health"
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                    location.pathname.startsWith('/admin/system-health')
                      ? 'bg-cyan-600 text-white'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  System Health
                </Link>
              )}
              {user.role === 'ADMIN' && (
                <Link
                  to="/admin/market-screening"
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                    location.pathname.startsWith('/admin/market-screening')
                      ? 'bg-cyan-600 text-white'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  Market Screening
                </Link>
              )}
              <Link
                to="/settings"
                onClick={closeMobileMenu}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors min-h-touch ${
                  location.pathname === '/settings'
                    ? 'bg-cyan-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
              >
                Settings
              </Link>
            </div>

            {/* Logout Button */}
            <div className="p-4 border-t border-neutral-800">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors min-h-touch"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNav;
